import { z } from 'zod';
import { idSchema, timestampsSchema } from './common';

export const triggerTypes = ['teams', 'email', 'webhook', 'schedule', 'meeting'] as const;
export const triggerTypeSchema = z.enum(triggerTypes);
export type TriggerType = z.infer<typeof triggerTypeSchema>;

export const triggerStatuses = ['active', 'configured', 'disabled'] as const;
export const triggerStatusSchema = z.enum(triggerStatuses);
export type TriggerStatus = z.infer<typeof triggerStatusSchema>;

/**
 * Per-type trigger configuration stored as a JSONB column. Uses `.strict()` so
 * unknown keys are rejected (catches typos like `publishIntegratonId`).
 * Per-type requirements (e.g. cron for schedule) are enforced by
 * `validateTriggerConfig()` which should be called at the service layer.
 */
export const triggerConfigSchema = z
  .object({
    /** Email address / webhook source / Teams channel, depending on type. */
    address: z.string().optional(),
    /** Cron expression for `schedule` triggers. */
    cron: z.string().optional(),
    /** Free-form source label. */
    source: z.string().optional(),
    /** Vault ref used to verify inbound ingress signatures. */
    secretRef: z.string().optional(),
    /** Integration used to publish the outcome of runs started by this trigger (Epic O5). */
    publishIntegrationId: z.string().optional(),
  })
  .strict();
export type TriggerConfig = z.infer<typeof triggerConfigSchema>;

/** Cron-like expression: 5 or 6 space-separated fields. */
const CRON_RE = /^(\*|[0-9,/\\-]+)\s+(\*|[0-9,/\\-]+)\s+(\*|[0-9,/\\-]+)\s+(\*|[0-9,/\\-]+)\s+(\*|[0-9,/\\-]+)(\s+(\*|[0-9,/\\-]+))?$/;

/**
 * Validates that a trigger's config matches the requirements for its type.
 * Throws on failure. Call this from service-layer create/update.
 */
export function validateTriggerConfig(type: TriggerType, config: unknown): TriggerConfig {
  const parsed = triggerConfigSchema.parse(config);
  if (type === 'schedule') {
    if (!parsed.cron) {
      throw new z.ZodError([
        { code: 'invalid_type', expected: 'string', received: 'undefined', path: ['cron'], message: 'Schedule triggers require a cron expression' },
      ]);
    }
    if (!CRON_RE.test(parsed.cron.trim())) {
      throw new z.ZodError([
        { code: 'custom', path: ['cron'], message: 'Invalid cron expression: expected 5-6 space-separated fields' },
      ]);
    }
  }
  return parsed;
}

export const triggerSchema = z
  .object({
    id: idSchema,
    tenantId: idSchema,
    workspaceId: idSchema,
    type: triggerTypeSchema,
    name: z.string().min(1).max(200),
    status: triggerStatusSchema.default('configured'),
    config: triggerConfigSchema.default({}),
    /** Orchestrator entrypoint started when this trigger fires. */
    targetAgentId: idSchema.nullish(),
  })
  .merge(timestampsSchema);
export type Trigger = z.infer<typeof triggerSchema>;

export const createTriggerSchema = triggerSchema
  .pick({ type: true, name: true, config: true })
  .extend({
    status: triggerStatusSchema.optional(),
    targetAgentId: idSchema.nullish(),
  });
export type CreateTrigger = z.infer<typeof createTriggerSchema>;

export const updateTriggerSchema = createTriggerSchema.partial();
export type UpdateTrigger = z.infer<typeof updateTriggerSchema>;

/** Payload accepted by the public intake ingress. */
export const intakePayloadSchema = z
  .object({
    text: z.string().optional(),
    message: z.string().optional(),
    subject: z.string().optional(),
    body: z.string().optional(),
    from: z.string().optional(),
  })
  .passthrough();
export type IntakePayload = z.infer<typeof intakePayloadSchema>;

export const intakeResultSchema = z.object({
  runId: idSchema,
  agentId: idSchema,
  label: z.string().optional(),
  matched: z.boolean(),
});
export type IntakeResult = z.infer<typeof intakeResultSchema>;

